import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Dropdown, Input, Select, Tooltip } from 'antd';

import useSwr from '@hooks/useSwr';
import { useTranslation } from 'react-i18next';
import Table from '@components/Table';
import {
  ArrowRightOutlined,
  EllipsisOutlined,
  FileSearchOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';

import {
  ServiceRequestData,
  ServiceRequestsResponse,
} from '@services/types/Relationship/MyCalls/types';
import { dateFormat } from '@utils/formatters';
import { CustomerData } from '@services/types/General/customer';
import useSearchParams from '@hooks/useSearchParams';
import FloatingButton from '@components/FloatingButton';
import { AiOutlineWhatsApp } from 'react-icons/ai';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import moment from 'moment';
import CustomTag from '@components/CustomTag';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import NewServiceRequest from './NewServiceRequest';
import ServiceRequestDetails from './ServiceRequestDetails';
import { MessagesActionNames } from './ServiceRequestDetails/RequestMessages/MessagesHeader/MessagesActions';

import * as S from './styles';

const { Search } = Input;

const ServiceRequests = () => {
  const [openDrawerDetails, setOpenDrawerDetails] = useState(false);
  const [openModalNewRequest, setOpenModalNewRequest] = useState(false);
  const [createdByValue, setCreatedByValue] = useState<string>();
  const [protocolValue, setProtocolValue] = useState<string>();
  const [requestDetailsId, setRequestIdDetails] = useState<number>();

  const { t } = useTranslation(['service-requests', 'device-actions-modal']);
  const { keycloak } = useContext(KeycloakContext);
  const customerTypeLogged = keycloak.idTokenParsed?.customerType;

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['startDate', 'endDate'],
    extraParams: [
      'contract',
      'protocol',
      'openingDate',
      'createdBy',
      'type',
      'status',
      'subStatus',
    ],
  });

  const {
    contract,
    protocol,
    openingDate,
    createdBy,
    status,
    type,
    subStatus,
    startDate,
    endDate,
    page,
  } = useMemo(() => searchParams, [searchParams]);

  useEffect(() => {
    if (!startDate && !endDate) {
      const lastWeek = moment().subtract(2, 'weeks').format('DDMMYYYY');
      const today = moment().format('DDMMYYYY');

      setSearchParams({
        startDate: lastWeek,
        endDate: today,
      });
    }
  }, [endDate, setSearchParams, startDate]);

  const formatDate = useCallback(
    (date: string) => moment(date, 'DDMMYYYY')?.format('DD/MM/YYYY'),
    [],
  );

  const filters = useMemo(
    () => ({
      contract,
      protocol,
      openingDate,
      createdBy,
      status,
      type,
      subStatus,
      startDate: startDate ? formatDate(startDate) : undefined,
      endDate: endDate ? formatDate(endDate) : undefined,
      page: page ? Number(page) : 1,
      maxLimit: 20,
      linesPerPage: 20,
    }),
    [
      contract,
      protocol,
      openingDate,
      createdBy,
      status,
      type,
      subStatus,
      startDate,
      formatDate,
      endDate,
      page,
    ],
  );

  const { data, mutate } = useSwr<ServiceRequestsResponse>(
    '/service-proxy/service/request/findServiceRequest',
    {
      method: 'post',
      params: filters,
    },
  );

  const handleDateChange = useCallback(
    (values: any) => {
      const start = moment(values.startDate).format('DDMMYYYY');
      const end = moment(values.endDate).format('DDMMYYYY');
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const statusOptions = useMemo(
    () => [
      {
        label: t('status.open'),
        value: 'ABERTO/EM ANDAMENTO',
      },
      {
        label: t('status.openInterrupted'),
        value: 'ABERTO/INTERROMPIDO',
      },
      {
        label: t('status.closed'),
        value: 'FECHADO/FECHADO',
      },
      {
        label: t('status.closedUnfounded'),
        value: 'FECHADO/IMPROCEDENTE',
      },
    ],
    [t],
  );

  const requests = useMemo(
    () =>
      data?.serviceRequests?.map(service => {
        const statusLabel =
          statusOptions?.find(
            opt =>
              opt.value === `${service?.status}/${service?.subStatus?.name}`,
          )?.label ?? '';

        const treatedStatus = {
          value: service?.status === 'ABERTO',
          label: statusLabel,
        };

        return { ...service, treatedStatus };
      }),
    [data?.serviceRequests, statusOptions],
  );

  const renderSrTypeColumn = useCallback(
    (srType1: { name: string }, record: ServiceRequestData) => {
      const allTypes = [
        record?.type2?.name,
        record?.type3?.name,
        record?.type4?.name,
      ]?.filter(item => item);

      const renderTypeLabel = (item: string) =>
        t(`types.${item.toLowerCase()?.replaceAll(' ', '')}`)?.toUpperCase();

      const dropdownItems = allTypes?.map(item => ({
        key: item,
        label: (
          <>
            <ArrowRightOutlined style={{ marginRight: 8 }} />
            <CustomTag>{renderTypeLabel(item)}</CustomTag>
          </>
        ),
      }));

      return (
        <>
          <CustomTag>{renderTypeLabel(srType1?.name)}</CustomTag>
          <Dropdown
            menu={{
              items: dropdownItems,
            }}
          >
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </>
      );
    },
    [t],
  );

  const renderStatusColumn = useCallback(
    (
      statusSelected: ServiceRequestData['status'],
      record: ServiceRequestData,
    ) => {
      const statusLabel = statusOptions?.find(
        opt => opt.value === `${statusSelected}/${record?.subStatus?.name}`,
      )?.label;

      return (
        <CustomTag color={statusSelected === 'ABERTO' ? 'green' : 'red'}>
          {statusLabel?.toUpperCase()}
        </CustomTag>
      );
    },
    [statusOptions],
  );

  const columns = useMemo(
    () => [
      {
        title: t('details'),
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: ServiceRequestData) => (
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              setRequestIdDetails(record?.id);
              setOpenDrawerDetails(true);
            }}
          />
        ),
      },
      {
        title: t('status.title'),
        dataIndex: 'status',
        align: 'center' as const,
        render: renderStatusColumn,
      },
      {
        title: t('srType1'),
        dataIndex: 'type1',
        render: renderSrTypeColumn,
      },
      {
        title: t('protocol'),
        dataIndex: 'protocol',
      },
      {
        title: t('assetNumber'),
        dataIndex: 'asset',
        render: (value: { assetNumber: string }) => value.assetNumber,
      },
      ...(customerTypeLogged === 'ROOT'
        ? [
            {
              title: t('customer'),
              dataIndex: 'customer',
              render: (customer: CustomerData) => customer?.name ?? '',
            },
          ]
        : []),
      {
        title: t('requester'),
        dataIndex: 'createdBy',
      },
      {
        title: t('openingDate'),
        dataIndex: 'createdAt',
        render: (value: string) => dateFormat(value),
      },
      {
        title: t('closingDate'),
        dataIndex: 'finishedAt',
        render: (value: string) => value && dateFormat(value),
      },
    ],
    [t, customerTypeLogged, renderSrTypeColumn, renderStatusColumn],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('requester'),
        element: (
          <Search
            onSearch={(value: string) => setSearchParams({ createdBy: value })}
            onChange={({ target: { value } }) => setCreatedByValue(value)}
            value={createdByValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('protocol'),
        element: (
          <Search
            onSearch={(value: string) => setSearchParams({ protocol: value })}
            onChange={({ target: { value } }) => setProtocolValue(value)}
            value={protocolValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('status.title'),
        element: (
          <Select
            optionFilterProp="label"
            options={statusOptions}
            style={{ width: '100%' }}
            value={status && subStatus && `${status}/${subStatus}`}
            onChange={value => {
              setSearchParams({
                status: value?.split('/')[0],
                subStatus: value?.split('/')[1],
              });
            }}
            allowClear
          />
        ),
      },
    ],
    [
      t,
      createdByValue,
      protocolValue,
      statusOptions,
      status,
      subStatus,
      setSearchParams,
    ],
  );

  const handleMutateRequest = useCallback(
    (action: MessagesActionNames, requestId: number) => {
      if (data) {
        mutate({
          ...data,
          serviceRequests: data?.serviceRequests?.map(request =>
            request.id === requestId
              ? {
                  ...request,
                  status: 'FECHADO',
                  subStatus: {
                    ...request.subStatus,
                    name: action === 'CLOSE' ? 'FECHADO' : 'IMPROCEDENTE',
                  },
                }
              : request,
          ),
        });
      }
    },
    [mutate, data],
  );

  const requestDetails = useMemo(
    () => requests?.find(request => request.id === requestDetailsId),
    [requestDetailsId, requests],
  );

  const renderCustomPagination = useMemo(() => {
    const currentPage = page ? Number(page) : 1;
    const isLastPage = (data?.recordsInPage ?? 20) < (data?.maxLimit ?? 20);
    const isOnlyOnePage = currentPage === 1 && isLastPage;
    return !data || isOnlyOnePage ? (
      <></>
    ) : (
      <S.CustomPagination>
        {currentPage > 1 && (
          <Button onClick={() => setSearchParams({ page: currentPage - 1 })}>
            Anterior
          </Button>
        )}
        <Button
          type="primary"
          disabled={isLastPage}
          onClick={() => setSearchParams({ page: currentPage + 1 })}
        >
          Próxima
        </Button>
      </S.CustomPagination>
    );
  }, [data, page, setSearchParams]);

  return (
    <div>
      <ServiceRequestDetails
        request={requestDetails}
        open={openDrawerDetails}
        onClose={() => setOpenDrawerDetails(false)}
        onMutateRequest={handleMutateRequest}
      />

      <RoleChecker role={rolesConstants.MANAGE_SERVICE_REQUESTS}>
        <NewServiceRequest
          open={openModalNewRequest}
          onClose={() => setOpenModalNewRequest(false)}
          onSuccess={mutate}
        />
      </RoleChecker>

      <FloatingButton
        icon={<AiOutlineWhatsApp />}
        title={t('msgs.whatsappButtonTitle')}
        onClick={() => {
          window.open(`https://wa.me/5534998892822`);
        }}
      />

      <Table
        columns={columns}
        data={requests ?? []}
        loading={!data}
        title={t('title')}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        tableKey="serviceRequests"
        noPagination
        customPagination={renderCustomPagination}
        onTableChange={setSearchParams}
        extraActions={
          <>
            <QuickRangePicker
              onChange={handleDateChange}
              value={
                startDate
                  ? [moment(startDate, 'DDMMYYYY'), moment(endDate, 'DDMMYYYY')]
                  : undefined
              }
              style={{ maxWidth: 240 }}
            />
            {customerTypeLogged !== 'ROOT' && (
              <RoleChecker role={rolesConstants.MANAGE_SERVICE_REQUESTS}>
                <Tooltip title={t('newRequest')}>
                  <Button
                    icon={<PlusCircleOutlined />}
                    onClick={() => setOpenModalNewRequest(true)}
                  />
                </Tooltip>
              </RoleChecker>
            )}
          </>
        }
      />
    </div>
  );
};

export default ServiceRequests;
